import api from "./api";

const TOKEN_KEY = "autocruise_token";
const GATE_KEY = "autocruise_gate";

/* Storage can throw in private-browsing modes, so every access is guarded. */
const read = (store, key) => {
  try {
    return store.getItem(key) || "";
  } catch (e) {
    return "";
  }
};
const write = (store, key, value) => {
  try {
    if (value) store.setItem(key, value);
    else store.removeItem(key);
  } catch (e) {
    /* non-fatal: the session simply will not survive a reload */
  }
};

/* The access token outlives a tab; the gate token deliberately does not. */
export const getToken = () => read(window.localStorage, TOKEN_KEY);
export const setToken = (t) => write(window.localStorage, TOKEN_KEY, t);
export const clearToken = () => write(window.localStorage, TOKEN_KEY, "");

export const getGateToken = () => read(window.sessionStorage, GATE_KEY);
export const setGateToken = (t) => write(window.sessionStorage, GATE_KEY, t);
export const clearGateToken = () => write(window.sessionStorage, GATE_KEY, "");

let onUnauthorized = () => {};
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthAttempt = url.includes("/auth/login") || url.includes("/auth/gate")
      || url.includes("/auth/register") || url.includes("/auth/invite");
    if (status === 401 && !isAuthAttempt) {
      clearToken();
      onUnauthorized();
    }
    return Promise.reject(err);
  }
);

export const apiError = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.detail || fallback;

export const openGate = async (pin) => {
  const { data } = await api.post("/auth/gate", { pin });
  setGateToken(data.gate_token);
  return data;
};

export const login = async (username, password, remember) => {
  const gate = getGateToken();
  const { data } = await api.post(
    "/auth/login",
    { username, password, remember },
    gate ? { headers: { "X-Gate-Token": gate } } : undefined
  );
  setToken(data.access_token);
  return data;
};

export const fetchAuthStatus = async () => (await api.get("/auth/status")).data;

export const register = async (payload) => {
  const { data } = await api.post("/auth/register", payload);
  setToken(data.access_token);
  return data;
};

export const acceptInvite = async (payload) => {
  const { data } = await api.post("/auth/invite/accept", payload);
  setToken(data.access_token);
  return data;
};

/* Moving between workspaces swaps the token: every request is scoped to the
   workspace named inside it, so the token IS the choice. */
export const switchWorkspace = async (workspaceId) => {
  const { data } = await api.post("/auth/switch", { workspace_id: workspaceId });
  setToken(data.access_token);
  return data;
};

export const enterWorkspaceAsAdmin = async (workspaceId) => {
  const { data } = await api.post(`/admin/workspaces/${workspaceId}/enter`);
  setToken(data.access_token);
  return data;
};

export const logout = async () => {
  try {
    await api.post("/auth/logout");
  } catch (e) {
    /* the token is being discarded either way */
  }
  clearToken();
  clearGateToken();
};

/* Full-page redirects (the OAuth "connect account" flows) cannot carry an
   Authorization header, so the token rides along as a query parameter. */
export const authedRedirectUrl = (path) => {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
  return `${base}${path.includes("?") ? "&" : "?"}t=${encodeURIComponent(getToken())}`;
};
